import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Chevron } from '../../components/atoms/Chevron'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import {
  LANDINGPATHPATTERN,
  USERTYPECODEPATTERN,
  USERTYPEDESCMAX,
  USERTYPENAMEMAX
} from '../../../constants/user-type.constants'
import type {
  CreateUserTypeInput,
  GroupCatalogueRow,
  UpdateUserTypeInput,
  UserTypeDto
} from '../../../viewmodels/useUserTypes'

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: UserTypeDto | null   // null when mode === 'create'
  readonly groups: readonly GroupCatalogueRow[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateUserTypeInput | UpdateUserTypeInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  code: string
  name: string
  description: string
  landing_path: string
  is_active: boolean
  group_ids: Set<number>
}

const EMPTY: FormState = {
  code: '',
  name: '',
  description: '',
  landing_path: '',
  is_active: true,
  group_ids: new Set()
}

function clientValidate(state: FormState, mode: 'create' | 'edit'): { field: keyof FormState | null; message: string } | null {
  if (mode === 'create') {
    if (!USERTYPECODEPATTERN.test(state.code)) {
      return { field: 'code', message: `code must match ${USERTYPECODEPATTERN.source}` }
    }
  }
  if (state.name.length < 1 || state.name.length > USERTYPENAMEMAX) {
    return { field: 'name', message: `name must be 1..${USERTYPENAMEMAX} chars` }
  }
  if (state.description.length > USERTYPEDESCMAX) {
    return { field: 'description', message: `description must be ≤${USERTYPEDESCMAX} chars` }
  }
  if (!LANDINGPATHPATTERN.test(state.landing_path)) {
    return { field: 'landing_path', message: 'landing_path must start with / and be a valid path' }
  }
  return null
}

/**
 * UserTypeFormModal — create + edit shared form. Field-level validation
 * runs client-side first; server-side errors map back onto fields via
 * the `error.field` returned by the API.
 *
 * `code` is editable only in `create` mode (Rule #25: code is immutable
 * after create).
 *
 * 2026-05-14: direct screens picker replaced by groups picker per
 * migration 0028 — Layer 2c is the only permissions path now.
 */
export function UserTypeFormModal({
  open,
  mode,
  initial,
  groups,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? { ...EMPTY, group_ids: new Set() }
          : {
              code: initial.code,
              name: initial.name,
              description: initial.description ?? '',
              landing_path: initial.landing_path ?? '',
              is_active: initial.is_active === 1,
              group_ids: new Set(initial.group_ids)
            }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  /** Alphabetical group list by name. */
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.name.localeCompare(b.name)),
    [groups]
  )

  const toggleGroup = (id: number): void => {
    setState((s) => {
      const next = new Set(s.group_ids)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return { ...s, group_ids: next }
    })
  }

  const toggleAllGroups = (ids: ReadonlyArray<number>, shouldSelect: boolean): void => {
    setState((s) => {
      const next = new Set(s.group_ids)
      if (shouldSelect) for (const id of ids) next.add(id)
      else for (const id of ids) next.delete(id)
      return { ...s, group_ids: next }
    })
  }

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state, mode)
    if (clientErr !== null) {
      if (clientErr.field !== null) setFieldError({ field: clientErr.field, message: clientErr.message })
      else setFormError(clientErr.message)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const group_ids = Array.from(state.group_ids).sort((a, b) => a - b)
    const payload: CreateUserTypeInput | UpdateUserTypeInput =
      mode === 'create'
        ? {
            code: state.code,
            name: state.name,
            description: state.description === '' ? null : state.description,
            landing_path: state.landing_path,
            is_active: state.is_active,
            group_ids
          }
        : {
            name: state.name,
            description: state.description === '' ? null : state.description,
            landing_path: state.landing_path,
            is_active: state.is_active,
            group_ids
          }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add user type' : `Edit ${initial?.code ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Code"
          helper="lowercase, digits, underscore. 2–32 chars. Immutable after create."
          error={fieldError?.field === 'code' ? fieldError.message : null}
        >
          <Input
            value={state.code}
            disabled={mode === 'edit'}
            hasError={fieldError?.field === 'code'}
            onChange={(e) => setState((s) => ({ ...s, code: e.target.value }))}
            placeholder="e.g. sale_manager"
          />
        </Field>

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="Display name"
            maxLength={USERTYPENAMEMAX}
          />
        </Field>

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={state.description}
            rows={2}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={USERTYPEDESCMAX}
          />
        </Field>

        <Field
          label="Landing path"
          helper="Post-login destination for users with this type. Must start with /."
          error={fieldError?.field === 'landing_path' ? fieldError.message : null}
        >
          <Input
            value={state.landing_path}
            hasError={fieldError?.field === 'landing_path'}
            onChange={(e) => setState((s) => ({ ...s, landing_path: e.target.value }))}
            placeholder="e.g. /dashboard"
          />
        </Field>

        <Checkbox
          label="Active"
          checked={state.is_active}
          onChange={(e) => setState((s) => ({ ...s, is_active: e.target.checked }))}
        />

        <GroupsPicker
          groups={sortedGroups}
          selected={state.group_ids}
          onToggle={toggleGroup}
          onToggleAll={toggleAllGroups}
          adminNotice={initial?.code === 'admin'}
        />
      </div>
    </Modal>
  )
}

function GroupsPicker({
  groups,
  selected,
  onToggle,
  onToggleAll,
  adminNotice
}: {
  readonly groups: ReadonlyArray<GroupCatalogueRow>
  readonly selected: ReadonlySet<number>
  readonly onToggle: (id: number) => void
  readonly onToggleAll: (ids: ReadonlyArray<number>, shouldSelect: boolean) => void
  readonly adminNotice: boolean
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return groups
    return groups.filter((g) => g.name.toLowerCase().includes(q))
  }, [groups, query])

  return (
    <div className="space-y-2 border-t border-border pt-4 mt-2" ref={wrapRef}>
      <div className="flex items-baseline justify-between">
        <span className="block text-body-sm font-body text-text-primary">Linked groups</span>
        <span className="text-body-xs text-text-secondary">{selected.size} selected</span>
      </div>
      {adminNotice ? (
        <Alert tone="info">
          The <span className="font-mono">admin</span> user_type has implicit access regardless of
          group membership. The selection here is informational for admin; other types are gated
          by the screen_actions in their linked groups.
        </Alert>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-border bg-white text-left font-body text-body-sm text-text-primary hover:border-forest/50 transition"
      >
        <span>
          {selected.size === 0
            ? 'Select groups…'
            : `${selected.size} group${selected.size === 1 ? '' : 's'} selected`}
        </span>
        <Chevron open={open} />
      </button>

      {open ? (
        <div className="rounded-md border border-border bg-bg-primary shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by group name…"
            />
          </div>
          {filtered.length > 0 ? (() => {
            const filteredIds = filtered.map((g) => g.id)
            const selectedInFiltered = filteredIds.filter((id) => selected.has(id)).length
            const allSelected = selectedInFiltered === filteredIds.length
            const someSelected = selectedInFiltered > 0 && !allSelected
            const label =
              query.trim() === ''
                ? allSelected ? 'Deselect all' : 'Select all'
                : allSelected ? `Deselect all matching (${filteredIds.length})` : `Select all matching (${filteredIds.length})`
            return (
              <label className="flex items-center gap-2 px-3 py-2 bg-parchment/40 border-b border-border cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => onToggleAll(filteredIds, !allSelected)}
                />
                <span className="font-body text-body-sm text-text-primary">{label}</span>
              </label>
            )
          })() : null}
          <div className="overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 font-body text-body-sm text-text-secondary">
                No groups match “{query}”.
              </p>
            ) : (
              filtered.map((g) => (
                <label
                  key={g.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-parchment/40 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(g.id)}
                    onChange={() => onToggle(g.id)}
                  />
                  <span className="font-body text-body-sm text-text-primary">{g.name}</span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}


